import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdSchedule, 
  MdSearch,
  MdCalendarToday,
  MdPeople,
  MdPerson,
  MdVisibility,
  MdCheckCircle,
  MdEdit,
  MdLocationOn
} from 'react-icons/md';
import { normalizeSession } from '../utils/sessionUtils';
import { formatDate, isToday } from '../utils/dateUtils';

function getBatchesArray(batches) {
  if (!batches) return [];
  if (Array.isArray(batches)) {
    return batches.map(b => typeof b === 'string' ? b : (b.name || b));
  }
  if (typeof batches === 'string') {
    return batches.split(',').map(b => b.trim()).filter(b => b);
  }
  return [];
}

function getDepartmentName(session, normalized) {
  return (
    normalized?.department ||
    normalized?.department_name ||
    session?.department ||
    session?.department_name ||
    session?.dept_name ||
    session?.department?.name ||
    session?.department_id?.name ||
    session?.batch_department ||
    session?.batch_department_name ||
    'Unknown Department'
  );
}

function getBatchNames(session, normalized) {
  const batches = normalized?.batches || session?.batches || session?.batch || session?.batch_name || [];
  const list = getBatchesArray(batches);
  if (list.length > 0) return list;
  if (typeof batches === 'string' && batches.trim()) return [batches.trim()];
  return ['Unknown Batch'];
}

const ATTENDANCE_FILTER_KEYS = ['from_date', 'to_date', 'teaching_week', 'campus', 'batch_id', 'state', 'search'];

function buildAttendanceFilterParams(filters) {
  const params = new URLSearchParams();

  ATTENDANCE_FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  return params;
}

/**
 * Faculty Attendance Component
 * Displays all scheduled Attendance for faculty batches
 */
function FacultySessions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAttendanceAuth();
  
  // Initialize with today's date immediately
  const today = new Date();
  const todayStr = formatDate(today);
  
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [teachingWeeks, setTeachingWeeks] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const [filters, setFilters] = useState(() => ({
    from_date: searchParams.get('from_date') || todayStr,
    to_date: searchParams.get('to_date') || todayStr,
    teaching_week: searchParams.get('teaching_week') || '',
    campus: searchParams.get('campus') || '',
    batch_id: searchParams.get('batch_id') || '',
    state: searchParams.get('state') || 'all',
    search: searchParams.get('search') || ''
  }));

  const updateFilters = (updates) => {
    const nextFilters = { ...filters, ...updates };
    setFilters(nextFilters);
    setSearchParams(buildAttendanceFilterParams(nextFilters), { replace: true });
  };

  const attendanceFilterParams = buildAttendanceFilterParams(filters).toString();
  const attendanceFilterSearch = attendanceFilterParams ? `?${attendanceFilterParams}` : '';

  useEffect(() => {
    // Fetch attendances when component mounts or filters change
    if (filters.from_date && filters.to_date) {
      fetchAttendances();
    }
  }, [filters.from_date, filters.to_date, filters.teaching_week, filters.campus, filters.batch_id, filters.state]);

  useEffect(() => {
    const token = getToken();
    attendanceAPI.getCampuses(token)
      .then(result => setCampuses(result.success ? (result.campuses || []) : []))
      .catch(err => console.error('Error loading campuses:', err));
    attendanceAPI.getBatches({}, token)
      .then(result => setBatches(result.success ? (result.batches || []) : []))
      .catch(err => console.error('Error loading batches:', err));
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAttendances = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const params = {};
      
      // Use filter dates, ensuring both are set to today by default
      const todayDate = formatDate(new Date());
      params.from_date = filters.from_date || todayDate;
      params.to_date = filters.to_date || todayDate;
      
      if (filters.batch_id) params.batch_id = parseInt(filters.batch_id);
      if (filters.teaching_week) params.teaching_week = parseInt(filters.teaching_week);
      if (filters.campus) params.campus = filters.campus;
      if (filters.state !== 'all') params.state = filters.state;

      const result = await attendanceAPI.getFacultySessions(params, token);
      
      if (result.success && result.sessions) {
        setTeachingWeeks(result.teaching_weeks || []);
        let filtered = result.sessions;
        
        // Filter attendances to only show those within the selected date range
        // This ensures that even if the API returns dates outside the range, we filter them correctly
        if (params.from_date && params.to_date) {
          filtered = filtered.filter(session => {
            const normalized = normalizeSession(session);
            if (normalized.session_date) {
              try {
                const sessionDateStr = formatDate(normalized.session_date);
                // Only keep sessions within the selected date range (inclusive)
                return sessionDateStr >= params.from_date && sessionDateStr <= params.to_date;
              } catch (e) {
                // If date parsing fails, keep the session (better to show than hide)
                console.warn('Could not parse session date:', normalized.session_date, e);
                return true;
              }
            }
            // If no date field, keep it (might be valid)
            return true;
          });
        }
        
        setAttendances(filtered);
      } else {
        setAttendances([]);
      }
    } catch (err) {
      console.error('Error fetching Attendance:', err);
      setError(err.message || 'Failed to load Attendance');
      setAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWeekChange = (event) => {
    const value = event.target.value;
    const week = teachingWeeks.find((item) => String(item.number) === value);
    updateFilters({
      teaching_week: value,
      ...(week ? { from_date: week.start_date, to_date: week.end_date } : {}),
    });
  };

  const getStateBadge = (state) => {
    const stateLower = state?.toLowerCase() || 'pending';
    switch (stateLower) {
      case 'marked':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
            Marked
          </span>
        );
      case 'missed':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
            Missed
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            Cancelled
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
    }
  };

  const isDesktopFilters = viewportWidth >= 1200;
  const isTabletFilters = viewportWidth >= 768 && viewportWidth < 1200;
  const filterRowStyle = isDesktopFilters
    ? { display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1.3fr 1.3fr 1.3fr 1fr', gap: '1rem', alignItems: 'center' }
    : isTabletFilters
      ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'center' }
      : { display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Attendance...</p>
        </div>
      </div>
    );
  }

  const searchLower = (filters.search || '').toLowerCase().trim();
  const visibleAttendances = searchLower
    ? attendances.filter(session => {
        const normalized = normalizeSession(session);
        const subject = (normalized.subject || '').toLowerCase();
        const batches = getBatchesArray(normalized.batches).join(' ').toLowerCase();
        const lecturer = (normalized.tutor_name || '').toLowerCase();
        const campus = (normalized.campus_name || '').toLowerCase();
        return subject.includes(searchLower) || batches.includes(searchLower) || lecturer.includes(searchLower) || campus.includes(searchLower);
      })
    : attendances;

  return (
    <div>
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 sm:mb-6 space-y-2 sm:space-y-0">
          <MdSchedule className="text-3xl sm:text-4xl text-blue-500 flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Faculty Attendance</h1>
            <p className="text-gray-600 text-xs sm:text-sm md:text-base mt-1">View all scheduled Attendance for faculty batches</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-gray-300 rounded-xl p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="relative w-full">
              <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                placeholder="Search Attendance..."
                value={filters.search}
                onChange={(e) => updateFilters({ search: e.target.value })}
                className="h-12 w-full pl-10 pr-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>

            <div style={filterRowStyle}>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => updateFilters({ from_date: e.target.value, teaching_week: '' })}
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="From Date"
              />

              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => updateFilters({ to_date: e.target.value, teaching_week: '' })}
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                placeholder="To Date"
              />

              <select
                value={filters.teaching_week}
                onChange={handleWeekChange}
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
              >
                <option value="">All Weeks</option>
                {teachingWeeks.map((week) => (
                  <option key={week.number} value={week.number}>{week.label}</option>
                ))}
              </select>

              <select
                value={filters.campus}
                onChange={(e) => updateFilters({ campus: e.target.value })}
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
              >
                <option value="">All Campuses</option>
                {campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
              </select>

              <select
                value={filters.batch_id}
                onChange={(e) => updateFilters({ batch_id: e.target.value })}
                aria-label="Filter attendance by batch"
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
              >
                <option value="">All Batches</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name || batch.code}</option>
                ))}
              </select>

              <select
                value={filters.state}
                onChange={(e) => updateFilters({ state: e.target.value })}
                className="h-12 w-full px-3 sm:px-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
              >
                <option value="all">All States</option>
                <option value="pending">Pending</option>
                <option value="marked">Marked</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Attendance List */}
      {visibleAttendances.length === 0 ? (
        <div className="bg-gray-50 border-2 border-gray-300 rounded-xl p-8 sm:p-12 text-center">
          <MdSchedule className="text-4xl sm:text-5xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Attendance Found</h3>
          <p className="text-sm sm:text-base text-gray-500">
            No Attendance found for the selected filters.
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {(() => {
            const grouped = new Map();

            visibleAttendances.forEach((session) => {
              const normalized = normalizeSession(session);
              const department = getDepartmentName(session, normalized);
              const cleanedDepartment =
                department &&
                String(department).trim() &&
                String(department).trim().toLowerCase() !== 'unknown department'
                  ? department
                  : '';
              const batchNames = getBatchNames(session, normalized);

              const departmentKey = cleanedDepartment ? cleanedDepartment : null;
              if (!grouped.has(departmentKey)) {
                grouped.set(departmentKey, new Map());
              }
              const deptMap = grouped.get(departmentKey);

              batchNames.forEach((batch) => {
                if (!deptMap.has(batch)) {
                  deptMap.set(batch, []);
                }
                deptMap.get(batch).push({ session, normalized });
              });
            });

            const departmentKeys = Array.from(grouped.keys());
            const namedDepartments = departmentKeys
              .filter((dept) => dept && String(dept).trim().toLowerCase() !== 'unknown department')
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
            const sortedDepartments = [...namedDepartments, ...departmentKeys.filter((dept) => !dept)];

            const output = [];

            sortedDepartments.forEach((department) => {
              const batchMap = grouped.get(department);
              const sortedBatches = Array.from(batchMap.keys()).sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
              );

              if (department && String(department).trim().toLowerCase() !== 'unknown department') {
                output.push(
                  <div key={`dept-${department}`} className="text-sm sm:text-base font-semibold text-gray-700 mt-2">
                    {department}
                  </div>
                );
              }

              sortedBatches.forEach((batch) => {
                output.push(
                  <div key={`batch-${department}-${batch}`} className="text-xs sm:text-sm font-medium text-gray-600">
                    Batch: {batch}
                  </div>
                );

                batchMap.get(batch).forEach(({ session, normalized }) => {
                  const batches = getBatchesArray(normalized.batches);
                  output.push(
                    <div
                      key={normalized.id}
                      className="bg-white border-2 border-gray-300 rounded-xl shadow-md hover:shadow-lg transition p-4 sm:p-6"
                    >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 break-words">
                            {normalized.subject}
                          </h3>
                          <div
                            className="flex items-center space-x-2 text-sm sm:text-base text-gray-600 mb-2 sm:mb-3"
                            title={normalized.tutor_name || 'Lecturer not assigned'}
                          >
                            <MdPerson className="text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-700">
                              Lecturer: {normalized.tutor_name || 'Not Assigned'}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                            {normalized.session_date && (
                              <div className="flex items-center space-x-1">
                                <MdCalendarToday className="text-gray-400 flex-shrink-0" />
                                <span>{new Date(normalized.session_date).toLocaleDateString()}</span>
                              </div>
                            )}
                            {normalized.time_start && normalized.time_end && (
                              <div className="flex items-center space-x-1">
                                <MdSchedule className="text-gray-400 flex-shrink-0" />
                                <span>{normalized.time_start} - {normalized.time_end}</span>
                              </div>
                            )}
                            {batches.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <MdPeople className="text-gray-400 flex-shrink-0" />
                                <span className="truncate">{batches.join(', ')}</span>
                              </div>
                            )}
                            {normalized.campus_name && (
                              <div className="flex items-center space-x-1">
                                <MdLocationOn className="text-gray-400 flex-shrink-0" />
                                <span>{normalized.campus_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-3">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {normalized.teaching_week && (
                              <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                                {normalized.teaching_week_label}
                              </span>
                            )}
                            {getStateBadge(normalized.state)}
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:space-x-2 w-full">
                            {normalized.state?.toLowerCase() !== 'marked' ? (
                              <button
                                onClick={() => navigate(`/attendance/faculty-admin/mark/${normalized.id}${attendanceFilterSearch}`)}
                                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md hover:shadow-lg"
                              >
                                <MdSchedule />
                                <span>Mark Attendance</span>
                              </button>
                            ) : (
                              <div className="flex w-full gap-2 sm:gap-3">
                                <button
                                  disabled
                                  className="flex-1 px-3 sm:px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md opacity-75"
                                  title="Attendance already marked"
                                >
                                  <MdCheckCircle />
                                  <span>Already Marked</span>
                                </button>
                                
                                <button
                                  onClick={() => navigate(`/attendance/faculty-admin/mark/${normalized.id}${attendanceFilterSearch}`)}
                                  className="flex-1 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md hover:shadow-lg"
                                  title="Edit marked attendance"
                                >
                                  <MdEdit />
                                  <span>Edit Attendance</span>
                                </button>
                              </div>
                            )}
                            
                            {normalized.can_view && (
                              <button
                                onClick={() => navigate(`/attendance/faculty-admin/session/${normalized.id}${attendanceFilterSearch}`)}
                                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md hover:shadow-lg"
                              >
                                <MdVisibility />
                                <span>View</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                });
              });
            });

            return output;
          })()}
        </div>
      )}

      {/* Summary */}
      {visibleAttendances.length > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Showing <span className="font-semibold">{visibleAttendances.length}</span> Attendance{visibleAttendances.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

export default FacultySessions;
